import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  createBidResponse,
  deleteBidResponse,
  getBidResponseById,
  getBidResponses,
  updateBidResponse,
} from '../../../../../shared/api/magellan/bidResponses';
import type {
  CreateBidResponseRequestDTO,
  GetBidResponsesResponseDTO,
  UpdateBidResponseRequestDTO,
} from '../../../../../shared/api/magellan/bidResponses/dto';
import { displayErrorNotification } from '../../../../../shared/components/notifications/errorNotification';
import type { BidResponse } from '../../../../../shared/entities/BidResponse';
import { RAGStatus } from '../../../../../shared/entities/RAGStatus';
import type { VaultProperty } from '../../../../../shared/entities/VaultProperty';
import { useWorkspaceUsersQuery } from '../../../../../shared/hooks/useWorkspaceUsersQuery.hook';
import { getVaultPropertiesQueryKey } from './vaultProperty.hook';

export type BidResponseTableRow = Omit<BidResponse, 'document'>;

export const getBidResponsesQueryKey = [getBidResponses.name];

export function useGetBidResponsesQuery() {
  return useSuspenseQuery({
    queryKey: getBidResponsesQueryKey,
    queryFn: () => getBidResponses(),
    refetchInterval: query => {
      const ragStatus = query.state.data?.ragStatus;
      const properties = query.state.data?.bidResponses.flatMap(
        bidResponse => bidResponse.properties,
      );

      const propertiesRagStatus = properties?.map(
        property => property.ragStatus,
      );

      const isRagRunning =
        (ragStatus && isInferring(ragStatus)) ||
        propertiesRagStatus?.some(isInferring);

      if (isRagRunning) {
        return 5000;
      }

      return false;
    },
  });
}

export function useGetBidResponseByIdQuery(bidResponseId: number) {
  return useSuspenseQuery({
    queryKey: [getBidResponseById.name, bidResponseId],
    queryFn: async () => await getBidResponseById({ bidResponseId }),
  });
}

export function useCreateBidResponseMutation() {
  const { t } = useTranslation(['notification', 'bidResponse']);
  const queryClient = useQueryClient();
  const { data: users } = useWorkspaceUsersQuery();

  return useMutation({
    mutationFn: (payload: CreateBidResponseRequestDTO) =>
      createBidResponse(payload),
    onMutate: async newBidResponseData => {
      await queryClient.cancelQueries({ queryKey: getBidResponsesQueryKey });
      const properties = queryClient.getQueryData<VaultProperty[]>(
        getVaultPropertiesQueryKey,
      );

      if (!properties) {
        return false;
      }

      const owner = users?.find(
        user => Number(user.id) === newBidResponseData.ownerId,
      );

      const newBidResponse = {
        ...newBidResponseData,
        responseDeadline: newBidResponseData.responseDeadline.toISOString(),
        properties: properties.map(property => ({
          values: [],
          propertyId: property.id,
        })),
        ragStatus: RAGStatus.WAITING,
        owner,
      };

      queryClient.setQueryData(
        getBidResponsesQueryKey,
        (old: GetBidResponsesResponseDTO) => ({
          ...old,
          bidResponses: [...old.bidResponses, newBidResponse].sort(
            sortBidResponses,
          ),
        }),
      );

      const previousBidResponses =
        queryClient.getQueryData<GetBidResponsesResponseDTO>(
          getBidResponsesQueryKey,
        );

      return { previousBidResponses };
    },
    onError: (_error, _variables, context) => {
      if (!context) {
        return;
      }

      queryClient.setQueryData(
        getBidResponsesQueryKey,
        context?.previousBidResponses,
      );
      displayErrorNotification(
        t('notification:errors.somethingWentWrong.title'),
        t('notification:errors.somethingWentWrong.message'),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getBidResponsesQueryKey });
    },
  });
}

export function useUpdateBidResponseMutation(bidResponseId: number) {
  const queryClient = useQueryClient();
  const { data: users } = useWorkspaceUsersQuery();

  return useMutation({
    mutationFn: (payload: UpdateBidResponseRequestDTO) =>
      updateBidResponse({ bidResponseId, payload }),
    onMutate: async payload => {
      await queryClient.cancelQueries({
        queryKey: [getBidResponseById.name, bidResponseId],
      });

      const oldData = queryClient.getQueryData<BidResponse>([
        getBidResponseById.name,
        bidResponseId,
      ]);

      if (!oldData) {
        return false;
      }

      const newOwner = users?.find(
        user => user.id === payload.ownerId?.toString(),
      );

      const newData: BidResponse = {
        ...oldData,
        owner: newOwner ?? oldData.owner,
        responseDeadline:
          payload.responseDeadline?.toISOString() ?? oldData.responseDeadline,
      };

      queryClient.setQueryData(
        [getBidResponseById.name, bidResponseId],
        newData,
      );

      return { oldData };
    },
    onError: (_error, _variables, context) => {
      if (!context) {
        return;
      }

      queryClient.setQueryData(
        [getBidResponseById.name, bidResponseId],
        context.oldData,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getBidResponseById.name] });
      queryClient.invalidateQueries({ queryKey: getBidResponsesQueryKey });
    },
  });
}

export function isInferring(ragStatus?: RAGStatus) {
  if (!ragStatus) {
    return false;
  }

  return [RAGStatus.PENDING, RAGStatus.RUNNING].includes(ragStatus);
}

type SortableBidResponse = Pick<BidResponse, 'responseDeadline' | 'title'>;

const sortBidResponses = (a: SortableBidResponse, b: SortableBidResponse) => {
  const aDeadline = new Date(a.responseDeadline).getTime();
  const bDeadline = new Date(b.responseDeadline).getTime();

  // Sort by deadline (newest first)
  if (aDeadline !== bDeadline) {
    return bDeadline - aDeadline;
  }

  // If deadlines are equal, sort by title alphabetically
  return a.title.localeCompare(b.title);
};

export function useDeleteBidResponseMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteBidResponse,
    onMutate: async bidResponseId => {
      await queryClient.cancelQueries({
        queryKey: [getBidResponseById.name, bidResponseId],
      });

      const previousBidResponse = queryClient.getQueryData<BidResponse>([
        getBidResponsesQueryKey,
        bidResponseId,
      ]);

      queryClient.setQueryData(
        getBidResponsesQueryKey,
        (old: GetBidResponsesResponseDTO) => ({
          ...old,
          bidResponses: old.bidResponses.filter(
            bidResponse => bidResponse.id !== bidResponseId,
          ),
        }),
      );

      return { previousBidResponse };
    },
    onError: (_error, _variables, context) => {
      if (!context) {
        return;
      }

      queryClient.setQueryData(
        getBidResponsesQueryKey,
        context.previousBidResponse,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getBidResponsesQueryKey });
    },
  });
}
